
import React, { useState } from 'react';
import { Job } from "@/components/jobs/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  Star, 
  MapPin, 
  Briefcase, 
  Clock, 
  Eye,
  MessageSquare,
  Phone,
  CheckCircle
} from 'lucide-react';
import { useJobOverviewNavigation } from '@/hooks/useJobOverviewNavigation';
import { useCandidateActions } from '@/hooks/useCandidateActions';
import { MessageDialog } from './MessageDialog';
import { CallDialog } from './CallDialog';

interface QuickCandidateAssessmentProps {
  job: Job;
}

export const QuickCandidateAssessment: React.FC<QuickCandidateAssessmentProps> = ({ job }) => {
  const { navigateToCandidateDetail, navigateToApplicationsTab } = useJobOverviewNavigation(job);
  const { advanceToScreening } = useCandidateActions();
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showCallDialog, setShowCallDialog] = useState(false);

  // Mock top candidates data (in real app, this would come from matching candidates API)
  const topCandidates = [
    {
      id: '1',
      name: 'Alex Johnson',
      title: 'Senior Software Engineer',
      location: 'San Francisco, CA',
      experience: '5 years',
      matchScore: 95,
      status: 'applied',
      appliedDays: 2,
      avatar: 'AJ',
      email: 'alex.johnson@email.com',
      phone: '+1-555-0123'
    },
    {
      id: '2', 
      name: 'Sarah Chen',
      title: 'Full Stack Developer',
      location: 'Seattle, WA',
      experience: '4 years',
      matchScore: 88,
      status: 'screening',
      appliedDays: 5,
      avatar: 'SC',
      email: 'sarah.chen@email.com',
      phone: '+1-555-0124'
    },
    {
      id: '3',
      name: 'Michael Rodriguez',
      title: 'Software Developer',
      location: 'Austin, TX',
      experience: '3 years',
      matchScore: 82,
      status: 'interviewing',
      appliedDays: 8,
      avatar: 'MR',
      email: 'michael.rodriguez@email.com',
      phone: '+1-555-0125'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'applied': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'screening': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'interviewing': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getMatchScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 dark:text-green-400';
    if (score >= 80) return 'text-blue-600 dark:text-blue-400';
    if (score >= 70) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const handleMessageClick = (candidate: any) => {
    setSelectedCandidate(candidate);
    setShowMessageDialog(true);
  };

  const handleCallClick = (candidate: any) => {
    setSelectedCandidate(candidate);
    setShowCallDialog(true);
  };

  const handleScreenClick = async (candidateId: string) => {
    await advanceToScreening(candidateId);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-primary" />
            Top Candidate Assessment
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {topCandidates.map((candidate, index) => (
            <div key={candidate.id} className="p-4 border rounded-lg hover:bg-muted/20 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {candidate.avatar}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-semibold">{candidate.name}</div>
                    <div className="text-sm text-muted-foreground">{candidate.title}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`text-lg font-bold ${getMatchScoreColor(candidate.matchScore)}`}>
                    {candidate.matchScore}%
                  </div>
                  <Badge className={getStatusColor(candidate.status)}>
                    {candidate.status}
                  </Badge>
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {candidate.location}
                </div>
                <div className="flex items-center gap-1">
                  <Briefcase className="h-3 w-3" />
                  {candidate.experience}
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Applied {candidate.appliedDays} days ago
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  className="flex-1"
                  onClick={() => navigateToCandidateDetail(candidate.id)}
                >
                  <Eye className="h-3 w-3 mr-1" />
                  Review
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleMessageClick(candidate)}
                >
                  <MessageSquare className="h-3 w-3 mr-1" />
                  Message
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleCallClick(candidate)}
                >
                  <Phone className="h-3 w-3 mr-1" />
                  Call
                </Button>
                {candidate.status === 'applied' && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="text-green-600"
                    onClick={() => handleScreenClick(candidate.id)}
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Screen
                  </Button>
                )}
              </div>
            </div>
          ))}

          <div className="pt-2 border-t">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={navigateToApplicationsTab}
            >
              View All {job.applicants || 24} Candidates
            </Button>
          </div>
        </CardContent>
      </Card>

      {selectedCandidate && (
        <>
          <MessageDialog
            isOpen={showMessageDialog}
            onClose={() => setShowMessageDialog(false)}
            candidateId={selectedCandidate.id}
            candidateName={selectedCandidate.name}
            candidateEmail={selectedCandidate.email}
          />
          <CallDialog
            isOpen={showCallDialog}
            onClose={() => setShowCallDialog(false)}
            candidateId={selectedCandidate.id}
            candidateName={selectedCandidate.name}
            candidatePhone={selectedCandidate.phone}
          />
        </>
      )}
    </>
  );
};
